import { CSSProperties } from "react";
import I18n from "common/i18n";
import { ProfileRow } from "./ProfileRow";
import { BEST_SUBJECT_OPTIONS, COLOR_PALETTE } from "../constants";

type EditingField = "color" | "bestSubjects" | "selfIntroduction";

type ProfileFavoritesTabProps = {
  favoriteColors: string[];
  bestSubjects: string[];
  selfIntroduction: string;
  onOpenEditModal: (field: EditingField) => void;
};

const renderSimpleValues = (
  values: string[],
  options: Array<{ key: string; labelKey: string }>,
): React.ReactNode => {
  if (values.length === 0) {
    return (
      <span className="type-body text-textcolor-black/50 block w-[400px]">
        {I18n.t("MSG_PROFILE_NOT_SET")}
      </span>
    );
  }
  const labels = values
    .map((v) => {
      const option = options.find((o) => o.key === v);
      return option ? I18n.t(option.labelKey) : v;
    })
    .join(", ");
  return <span className="type-body text-textcolor-black block w-[400px] truncate">{labels}</span>;
};

const renderColorValue = (favoriteColors: string[]): React.ReactNode => {
  if (favoriteColors.length === 0) {
    return (
      <span className="type-body text-textcolor-black/50 block w-[400px]">
        {I18n.t("MSG_PROFILE_NOT_SET")}
      </span>
    );
  }
  const color = favoriteColors[0];
  if (color.startsWith("#")) {
    return (
      <div className="flex-row-el items-center gap-2 w-[400px]">
        <span
          className="w-8 h-8 rounded-full border-2 border-gray-40 bg-[color:var(--swatch)]"
          style={{ "--swatch": color } as CSSProperties}
        />
      </div>
    );
  }
  return renderSimpleValues(favoriteColors, COLOR_PALETTE);
};

export const ProfileFavoritesTab = ({
  favoriteColors,
  bestSubjects,
  selfIntroduction,
  onOpenEditModal,
}: ProfileFavoritesTabProps) => (
  <div className="flex-col-el items-center w-full max-w-[900px] gap-4 px-4">
    <ProfileRow
      label={I18n.t("MSG_PROFILE_AI_FAVORITE_COLOR")}
      content={renderColorValue(favoriteColors)}
      onEdit={() => onOpenEditModal("color")}
    />
    <ProfileRow
      label={I18n.t("MSG_PROFILE_AI_BEST_SUBJECTS")}
      content={renderSimpleValues(bestSubjects, BEST_SUBJECT_OPTIONS)}
      onEdit={() => onOpenEditModal("bestSubjects")}
    />
    <ProfileRow
      label={I18n.t("MSG_PROFILE_AI_OTHER_INFO")}
      content={
        selfIntroduction ? (
          <span className="type-body text-textcolor-black block w-[400px] truncate">
            {selfIntroduction}
          </span>
        ) : (
          <span className="type-body text-textcolor-black/50 block w-[400px]">
            {I18n.t("MSG_PROFILE_SELF_INTRODUCTION_NOT_SET")}
          </span>
        )
      }
      onEdit={() => onOpenEditModal("selfIntroduction")}
    />
  </div>
);
